<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import {
  AGENT_FEATURE_PERMISSIONS,
  DEFAULT_AGENT_FEATURE_PERMISSIONS,
  DEFAULT_MANAGER_FEATURE_PERMISSIONS,
  FEATURE_PERMISSION_GROUPS,
  REPORT_DOWNLOAD_TO_VIEW,
  REPORT_GRANULAR_PERMISSIONS,
  REPORT_VIEW_PERMISSIONS,
  WEB_ORDER_ACCESS_PERMISSIONS,
  groupPermissionKeys,
} from 'dashboard/constants/permissions.js';
import Avatar from 'next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const saving = ref(false);
const selectedPermissions = ref([]);
const selectedRole = ref('agent');
const currentPassword = ref('');
const openGroups = ref({
  conversations: true,
  web_orders: true,
  reports: true,
  operations: false,
});
const openSections = ref(
  Object.fromEntries(
    FEATURE_PERMISSION_GROUPS.flatMap(group => group.sections || []).map(
      section => [section.key, true]
    )
  )
);

const uiFlags = useMapGetter('agents/getUIFlags');
const getAgentById = useMapGetter('agents/getAgentById');
const currentUser = useMapGetter('getCurrentUser');
const currentUserRole = useMapGetter('getCurrentRole');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const agentId = computed(() => Number(route.params.agentId));
const agent = computed(() => getAgentById.value(agentId.value) || {});

const isSelf = computed(
  () => Number(currentUser.value?.id) === Number(agent.value?.id)
);
const canEditRoleAndPermissions = computed(
  () => currentUserRole.value === 'administrator'
);
const hasAssignedCustomRole = computed(() =>
  Boolean(agent.value?.custom_role_id)
);
const assignedCustomRole = computed(() => {
  if (!hasAssignedCustomRole.value) return null;
  return (
    getCustomRoles.value.find(role => role.id === agent.value.custom_role_id) ||
    null
  );
});
const isAdministrator = computed(() => selectedRole.value === 'administrator');
const permissionsLocked = computed(
  () =>
    isAdministrator.value ||
    !canEditRoleAndPermissions.value ||
    hasAssignedCustomRole.value
);
const hasLocalPassword = computed(() => {
  if (currentUser.value?.password_login_enabled === false) return false;
  if (currentUser.value?.password_login_enabled === true) return true;
  const provider = currentUser.value?.provider;
  // email provider users authenticate with Devise password; others may be SSO/Firebase.
  return !provider || provider === 'email';
});

const permissionGroups = FEATURE_PERMISSION_GROUPS;

const permissionLabels = {
  conversation_manage: 'All conversations',
  conversation_unassigned_manage: 'Unassigned queue',
  conversation_participating_manage: 'Assigned to them',
  conversation_assign_self: 'Self-assign',
  conversation_assign: 'Assign to others',
  conversation_bulk_assign: 'Bulk assign',
  web_order_manage: 'View orders',
  web_order_status_change: 'Order status change',
  web_order_edit: 'Order edit',
  web_order_create: 'Add order',
  web_order_export: 'Export orders',
  web_order_products: 'View products',
  web_order_product_edit: 'Product edit',
  web_order_product_create: 'Add product',
  web_order_customers: 'View customers',
  web_order_customer_edit: 'Customer edit',
  web_order_invoice_settings: 'Invoice settings',
  report_own: 'View own reports',
  report_overview: 'Overview',
  report_overview_download: 'Download overview',
  report_conversation: 'Conversation',
  report_conversation_download: 'Download conversation reports',
  report_agents: 'Agent reports',
  report_agents_download: 'Download agent reports',
  report_inboxes: 'Inbox reports',
  report_inboxes_download: 'Download inbox reports',
  report_teams: 'Team reports',
  report_teams_download: 'Download team reports',
  report_labels: 'Label reports',
  report_labels_download: 'Download label reports',
  report_csat: 'CSAT',
  report_csat_download: 'Download CSAT',
  report_sla: 'SLA',
  report_sla_download: 'Download SLA',
  report_bot: 'Bot',
  contact_manage: 'Contacts',
  contact_export: 'Export contacts',
  knowledge_base_manage: 'Knowledge base',
  integration_manage: 'Integrations',
  user_manage: 'Manage users',
};

const defaultPermissionsForRole = role =>
  role === 'manager'
    ? DEFAULT_MANAGER_FEATURE_PERMISSIONS
    : DEFAULT_AGENT_FEATURE_PERMISSIONS;

const syncFromAgent = () => {
  if (!agent.value?.id) return;
  selectedRole.value = agent.value.role || 'agent';
  const saved = [
    ...(agent.value.feature_permissions?.length
      ? agent.value.feature_permissions
      : defaultPermissionsForRole(selectedRole.value)),
  ];
  if (saved.includes('report_manage')) {
    REPORT_GRANULAR_PERMISSIONS.forEach(permission => {
      if (!saved.includes(permission)) saved.push(permission);
    });
  }
  selectedPermissions.value = saved;
  currentPassword.value = '';
};

watch(agent, syncFromAgent, { immediate: true });

onMounted(async () => {
  if (!agent.value?.id) {
    await store.dispatch('agents/get');
  }
  store.dispatch('customRole/getCustomRole');
  syncFromAgent();
});

const goBack = () => {
  router.push({
    name: 'agent_list',
    params: { accountId: route.params.accountId },
  });
};

const toggleGroup = key => {
  openGroups.value = {
    ...openGroups.value,
    [key]: !openGroups.value[key],
  };
};

const toggleSection = key => {
  openSections.value = {
    ...openSections.value,
    [key]: !openSections.value[key],
  };
};

const isSelected = permission =>
  isAdministrator.value || selectedPermissions.value.includes(permission);

const allSelected = keys =>
  Boolean(keys?.length) && keys.every(permission => isSelected(permission));

const someSelected = keys =>
  Boolean(keys?.length) &&
  !allSelected(keys) &&
  keys.some(permission => selectedPermissions.value.includes(permission));

const bindTreeCheckbox = (el, keys) => {
  if (!el) return;
  el.indeterminate = someSelected(keys);
};

const applyPermissionSet = next => {
  const set = new Set(next);
  const previous = new Set(selectedPermissions.value);

  Object.entries(REPORT_DOWNLOAD_TO_VIEW).forEach(([download, view]) => {
    if (set.has(download) && !previous.has(download)) set.add(view);
    if (!set.has(view) && previous.has(view)) set.delete(download);
    if (set.has(download) && !set.has(view)) set.delete(download);
  });

  if (set.has('contact_export') && !previous.has('contact_export')) {
    set.add('contact_manage');
  }
  if (!set.has('contact_manage') && previous.has('contact_manage')) {
    set.delete('contact_export');
  }
  if (set.has('contact_export') && !set.has('contact_manage')) {
    set.delete('contact_export');
  }

  const hasOrderAccess = permission =>
    WEB_ORDER_ACCESS_PERMISSIONS.includes(permission);
  const orderAccessSelected = [...set].some(hasOrderAccess);
  const orderAccessWasSelected = [...previous].some(hasOrderAccess);
  if (set.has('web_order_export') && !previous.has('web_order_export')) {
    if (!orderAccessSelected) set.add('web_order_manage');
  }
  if (!orderAccessSelected && orderAccessWasSelected) {
    set.delete('web_order_export');
  }
  if (set.has('web_order_export') && ![...set].some(hasOrderAccess)) {
    set.delete('web_order_export');
  }

  const allReportLeavesSelected = REPORT_GRANULAR_PERMISSIONS.every(
    permission => set.has(permission)
  );
  if (allReportLeavesSelected) set.add('report_manage');
  else set.delete('report_manage');

  selectedPermissions.value = AGENT_FEATURE_PERMISSIONS.filter(permission =>
    set.has(permission)
  );
};

const togglePermission = permission => {
  if (permissionsLocked.value) return;
  const set = new Set(selectedPermissions.value);
  if (set.has(permission)) set.delete(permission);
  else set.add(permission);
  applyPermissionSet(set);
};

const toggleKeys = keys => {
  if (permissionsLocked.value || !keys?.length) return;
  const enable = !keys.every(permission =>
    selectedPermissions.value.includes(permission)
  );
  const set = new Set(selectedPermissions.value);
  keys.forEach(permission => {
    if (enable) set.add(permission);
    else set.delete(permission);
  });
  applyPermissionSet(set);
};

const applyRolePreset = () => {
  if (selectedRole.value === 'administrator') return;
  selectedPermissions.value = [
    ...defaultPermissionsForRole(selectedRole.value),
  ];
};

const selectRole = role => {
  if (!canEditRoleAndPermissions.value) return;
  selectedRole.value = role;
  applyRolePreset();
};

const hasReportAccess = permissions =>
  permissions.includes('report_manage') ||
  REPORT_VIEW_PERMISSIONS.some(permission => permissions.includes(permission));

const featurePermissionsForSave = () => {
  const current = selectedPermissions.value;
  if (selectedRole.value !== 'manager' || hasReportAccess(current)) {
    return [...current];
  }
  const merged = new Set([...current, ...DEFAULT_MANAGER_FEATURE_PERMISSIONS]);
  return AGENT_FEATURE_PERMISSIONS.filter(permission => merged.has(permission));
};

const willFillManagerDefaults = computed(
  () =>
    selectedRole.value === 'manager' &&
    !hasAssignedCustomRole.value &&
    !hasReportAccess(selectedPermissions.value)
);

const permissionsChanged = computed(() => {
  const baseline = [
    ...(agent.value.feature_permissions?.length
      ? agent.value.feature_permissions
      : defaultPermissionsForRole(agent.value.role || 'agent')),
  ].sort();
  const updated = [...selectedPermissions.value].sort();
  return baseline.join(',') !== updated.join(',');
});

const roleChanged = computed(
  () => selectedRole.value !== (agent.value.role || 'agent')
);

const stepUpRequired = computed(
  () =>
    canEditRoleAndPermissions.value &&
    hasLocalPassword.value &&
    (roleChanged.value ||
      willFillManagerDefaults.value ||
      (!isAdministrator.value &&
        permissionsChanged.value &&
        !hasAssignedCustomRole.value))
);

const saveProfile = async () => {
  if (!agent.value?.id) return;
  if (stepUpRequired.value && !currentPassword.value) {
    useAlert(t('AGENT_MGMT.PROFILE.STEP_UP_REQUIRED'));
    return;
  }

  saving.value = true;
  try {
    const payload = {
      id: agent.value.id,
      name: agent.value.name,
    };
    if (canEditRoleAndPermissions.value) {
      if (roleChanged.value) {
        payload.role = selectedRole.value;
        payload.custom_role_id = null;
      }
      const customRoleStillAssigned =
        hasAssignedCustomRole.value && !roleChanged.value;
      if (!isAdministrator.value && !customRoleStillAssigned) {
        const featurePermissions = featurePermissionsForSave();
        if (
          permissionsChanged.value ||
          willFillManagerDefaults.value ||
          (roleChanged.value && hasAssignedCustomRole.value)
        ) {
          payload.feature_permissions = featurePermissions;
        }
      }
    }
    if (stepUpRequired.value) {
      payload.current_password = currentPassword.value;
    }
    await store.dispatch('agents/update', payload);
    currentPassword.value = '';
    useAlert(t('AGENT_MGMT.PROFILE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const message = error?.message || '';
    if (/invalid current password/i.test(message)) {
      useAlert(t('AGENT_MGMT.ADD.API.INVALID_PASSWORD_MESSAGE'));
    } else if (/not authorized|unauthorized/i.test(message)) {
      useAlert(t('AGENT_MGMT.ADD.API.UNAUTHORIZED_MESSAGE'));
    } else {
      useAlert(message || t('AGENT_MGMT.PROFILE.API.ERROR_MESSAGE'));
    }
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <SettingsLayout :is-loading="uiFlags.isFetching && !agent.id">
    <template #header>
      <BaseSettingsHeader
        :title="agent.name || $t('AGENT_MGMT.PROFILE.TITLE')"
        :description="$t('AGENT_MGMT.PROFILE.DESC')"
      >
        <template #actions>
          <Button
            faded
            slate
            :label="$t('AGENT_MGMT.PROFILE.BACK')"
            @click="goBack"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div v-if="agent.id" class="mx-auto flex w-full max-w-2xl flex-col gap-5">
        <div
          class="flex items-center gap-4 rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3"
        >
          <Avatar
            :src="agent.thumbnail"
            :name="agent.name"
            :status="agent.availability_status"
            :size="56"
            hide-offline-status
          />
          <div class="min-w-0 flex-1">
            <p class="truncate text-base font-semibold text-n-slate-12">
              {{ agent.name }}
            </p>
            <p class="truncate text-sm text-n-slate-11">{{ agent.email }}</p>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ $t('AGENT_MGMT.PROFILE.AVATAR.HINT') }}
              <router-link
                v-if="isSelf"
                :to="{ name: 'profile_settings_index' }"
                class="font-medium text-n-brand hover:underline"
              >
                {{ $t('AGENT_MGMT.PROFILE.AVATAR.OPEN_OWN_PROFILE') }}
              </router-link>
            </p>
          </div>
        </div>

        <section class="rounded-xl border border-n-weak bg-n-solid-1 px-4 py-4">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ $t('AGENT_MGMT.PROFILE.ROLE_LABEL') }}
          </h3>
          <p class="mt-1 text-xs text-n-slate-11">
            {{ $t('AGENT_MGMT.PROFILE.ROLE_HINT') }}
          </p>
          <div class="mt-3 grid grid-cols-3 gap-2">
            <button
              type="button"
              class="rounded-lg border px-2 py-2.5 text-center text-sm font-medium transition"
              :disabled="!canEditRoleAndPermissions"
              :class="[
                selectedRole === 'agent'
                  ? 'border-n-brand bg-n-brand/10 text-n-brand'
                  : 'border-n-weak bg-n-background text-n-slate-11 hover:border-n-slate-6',
                { 'cursor-not-allowed opacity-60': !canEditRoleAndPermissions },
              ]"
              @click="selectRole('agent')"
            >
              {{ $t('AGENT_MGMT.AGENT_TYPES.AGENT') }}
            </button>
            <button
              type="button"
              class="rounded-lg border px-2 py-2.5 text-center text-sm font-medium transition"
              :disabled="!canEditRoleAndPermissions"
              :class="[
                selectedRole === 'manager'
                  ? 'border-n-brand bg-n-brand/10 text-n-brand'
                  : 'border-n-weak bg-n-background text-n-slate-11 hover:border-n-slate-6',
                { 'cursor-not-allowed opacity-60': !canEditRoleAndPermissions },
              ]"
              @click="selectRole('manager')"
            >
              {{ $t('AGENT_MGMT.AGENT_TYPES.MANAGER') }}
            </button>
            <button
              type="button"
              class="rounded-lg border px-2 py-2.5 text-center text-sm font-medium transition"
              :disabled="!canEditRoleAndPermissions"
              :class="[
                selectedRole === 'administrator'
                  ? 'border-n-brand bg-n-brand/10 text-n-brand'
                  : 'border-n-weak bg-n-background text-n-slate-11 hover:border-n-slate-6',
                { 'cursor-not-allowed opacity-60': !canEditRoleAndPermissions },
              ]"
              @click="selectRole('administrator')"
            >
              {{ $t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR') }}
            </button>
          </div>
          <p
            v-if="!canEditRoleAndPermissions"
            class="mt-2 text-xs text-n-slate-11"
          >
            {{ $t('AGENT_MGMT.PROFILE.ADMIN_ONLY_ROLE_PERMISSIONS') }}
          </p>
        </section>

        <section
          class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
        >
          <div class="border-b border-n-weak px-4 py-3">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ $t('AGENT_MGMT.PROFILE.PERMISSIONS_TITLE') }}
            </h3>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{
                isAdministrator
                  ? $t('AGENT_MGMT.PROFILE.ADMIN_FULL_ACCESS')
                  : hasAssignedCustomRole
                    ? $t('AGENT_MGMT.PROFILE.CUSTOM_ROLE_OWNS_PERMISSIONS', {
                        name: assignedCustomRole?.name || '',
                      })
                    : $t('AGENT_MGMT.PROFILE.PERMISSIONS_DESC_SHORT')
              }}
            </p>
          </div>

          <div
            v-for="group in permissionGroups"
            :key="group.key"
            class="border-b border-n-weak last:border-b-0"
          >
            <div
              class="flex w-full items-center gap-2 px-4 py-3 hover:bg-n-alpha-2"
            >
              <input
                v-if="group.sections"
                :ref="el => bindTreeCheckbox(el, groupPermissionKeys(group))"
                type="checkbox"
                class="size-4 shrink-0 rounded border-n-weak text-n-brand focus:ring-n-brand"
                :checked="allSelected(groupPermissionKeys(group))"
                :disabled="permissionsLocked"
                @click.stop
                @change="toggleKeys(groupPermissionKeys(group))"
              />
              <button
                type="button"
                class="flex min-w-0 flex-1 items-center justify-between gap-2 text-left"
                @click="toggleGroup(group.key)"
              >
                <span class="text-sm font-medium text-n-slate-12">
                  {{ group.title }}
                </span>
                <span
                  class="size-4 shrink-0 text-n-slate-9"
                  :class="
                    openGroups[group.key]
                      ? 'i-lucide-chevron-up'
                      : 'i-lucide-chevron-down'
                  "
                />
              </button>
            </div>

            <div
              v-if="openGroups[group.key]"
              :class="
                group.sections
                  ? 'px-3 pb-3'
                  : 'grid gap-1 px-3 pb-3 sm:grid-cols-2'
              "
            >
              <template v-if="group.sections">
                <div
                  v-for="section in group.sections"
                  :key="section.key"
                  class="select-none"
                >
                  <div class="flex items-center gap-2 rounded-md px-1 py-1.5">
                    <button
                      type="button"
                      class="grid size-4 shrink-0 place-items-center text-n-slate-11"
                      :aria-expanded="Boolean(openSections[section.key])"
                      @click="toggleSection(section.key)"
                    >
                      <span
                        class="size-3.5"
                        :class="
                          openSections[section.key]
                            ? 'i-lucide-chevron-down'
                            : 'i-lucide-chevron-right'
                        "
                      />
                    </button>
                    <input
                      :ref="el => bindTreeCheckbox(el, section.items)"
                      type="checkbox"
                      class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
                      :checked="allSelected(section.items)"
                      :disabled="permissionsLocked"
                      @change="toggleKeys(section.items)"
                    />
                    <button
                      type="button"
                      class="text-left text-sm font-medium text-n-slate-12"
                      @click="toggleSection(section.key)"
                    >
                      {{ section.title }}
                    </button>
                  </div>
                  <div v-if="openSections[section.key]" class="ms-7">
                    <label
                      v-for="permission in section.items"
                      :key="permission"
                      class="flex cursor-pointer items-center gap-2.5 rounded-md px-1 py-1.5 text-sm transition hover:bg-n-alpha-2"
                      :class="{
                        'pointer-events-none opacity-50': permissionsLocked,
                      }"
                    >
                      <input
                        type="checkbox"
                        class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
                        :checked="isSelected(permission)"
                        :disabled="permissionsLocked"
                        @change="togglePermission(permission)"
                      />
                      <span class="text-n-slate-12">
                        {{ permissionLabels[permission] || permission }}
                      </span>
                    </label>
                  </div>
                </div>
                <label
                  v-for="permission in group.items"
                  :key="permission"
                  class="mt-1 flex cursor-pointer items-center gap-2.5 rounded-md px-1 py-1.5 text-sm transition hover:bg-n-alpha-2"
                  :class="{
                    'pointer-events-none opacity-50': permissionsLocked,
                  }"
                >
                  <span class="size-4 shrink-0" />
                  <input
                    type="checkbox"
                    class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
                    :checked="isSelected(permission)"
                    :disabled="permissionsLocked"
                    @change="togglePermission(permission)"
                  />
                  <span class="text-n-slate-12">
                    {{ permissionLabels[permission] || permission }}
                  </span>
                </label>
              </template>
              <template v-else>
                <label
                  v-for="permission in group.items"
                  :key="permission"
                  class="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-sm transition hover:bg-n-alpha-2"
                  :class="{
                    'pointer-events-none opacity-50': permissionsLocked,
                  }"
                >
                  <input
                    type="checkbox"
                    class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
                    :checked="isSelected(permission)"
                    :disabled="permissionsLocked"
                    @change="togglePermission(permission)"
                  />
                  <span class="text-n-slate-12">
                    {{ permissionLabels[permission] || permission }}
                  </span>
                </label>
              </template>
            </div>
          </div>
        </section>

        <section
          v-if="stepUpRequired"
          class="rounded-xl border border-n-amber-6 bg-n-amber-2/30 px-4 py-3"
        >
          <label
            class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ $t('PROFILE_SETTINGS.FORM.CURRENT_PASSWORD.LABEL') }}
            <input
              v-model="currentPassword"
              type="password"
              autocomplete="current-password"
              class="reset-base m-0 !mb-0 h-10 w-full rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-brand"
              :placeholder="
                $t('PROFILE_SETTINGS.FORM.CURRENT_PASSWORD.PLACEHOLDER')
              "
            />
          </label>
          <p class="mt-1.5 text-xs text-n-slate-11">
            {{ $t('AGENT_MGMT.PROFILE.STEP_UP_DESC') }}
          </p>
        </section>

        <p
          v-else-if="
            canEditRoleAndPermissions &&
            (roleChanged || permissionsChanged || willFillManagerDefaults) &&
            !hasLocalPassword
          "
          class="rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3 text-xs text-n-slate-11"
        >
          {{ $t('AGENT_MGMT.PROFILE.STEP_UP_SKIPPED') }}
        </p>

        <div class="flex justify-end gap-2 pb-6">
          <Button
            faded
            slate
            :label="$t('AGENT_MGMT.PROFILE.CANCEL')"
            @click="goBack"
          />
          <Button
            :label="$t('AGENT_MGMT.PROFILE.SAVE')"
            :is-loading="saving || uiFlags.isUpdating"
            :disabled="saving || uiFlags.isUpdating"
            @click="saveProfile"
          />
        </div>
      </div>
      <div v-else class="py-16 text-center text-body-main text-n-slate-11">
        {{ $t('AGENT_MGMT.PROFILE.NOT_FOUND') }}
      </div>
    </template>
  </SettingsLayout>
</template>
